import { useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, CheckCircle, XCircle, Warning, FileText, ArrowRight, Notebook,
  Scales, Calculator, Bank as BankIcon, Truck, Cpu, Buildings, ShieldCheck,
} from "@phosphor-icons/react";
import OISCard from "@/components/OISCard";

const RECO_STYLE = {
  INVEST:  { bg: "bg-teal-soft",    text: "text-teal",    Icon: CheckCircle, band: "text-teal" },
  REVIEW:  { bg: "bg-amber-100",    text: "text-amber-700", Icon: Warning,    band: "text-amber-700" },
  DECLINE: { bg: "bg-red-100",      text: "text-red-700",   Icon: XCircle,    band: "text-red-700" },
};

const SEVERITY_STYLE = {
  High:   "bg-red-100 text-red-700",
  Medium: "bg-amber-100 text-amber-700",
  Low:    "bg-teal-soft text-teal",
};

/**
 * InvestmentCommitteeTab — renders the Investment Committee Paper generated
 * from the assessment dossier + OIS engine.
 * If not yet generated, offers a "Generate Investment Committee Paper" CTA.
 */
export default function InvestmentCommitteeTab({ assessmentId }) {
  const [state, setState] = useState({ loading: true, paper: null, ois: null, generated_at: null });
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/assessments/${assessmentId}/investment-committee`);
      setState({ loading: false, paper: data.committee, ois: data.ois, generated_at: data.generated_at });
    } catch (e) {
      setState({ loading: false, paper: null, ois: null, generated_at: null });
    }
  };

  useState(() => { load(); }, []);
  // Initial load using standard useEffect via lazy invocation
  if (state.loading && !state._triggered) {
    state._triggered = true;
    load();
  }

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/investment-committee`);
      setState({ loading: false, paper: data.committee, ois: data.ois, generated_at: new Date().toISOString() });
      toast.success("Investment Committee Paper generated.");
    } catch (e) {
      toast.error(extractApiError(e, "Could not generate the Investment Committee Paper."));
    }
    setGenerating(false);
  };

  if (state.loading) {
    return <div className="text-muted-foreground text-sm" data-testid="ic-loading">Loading Investment Committee Paper…</div>;
  }

  if (!state.paper) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 card-elev text-center" data-testid="ic-empty">
        <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center mb-5">
          <FileText size={22} weight="duotone" />
        </div>
        <h3 className="font-display text-2xl tracking-tight mb-2">Investment Committee Paper</h3>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
          Generate the institutional decision document — executive summary, thesis, risks &amp; mitigation, SWOT, entry strategy, expected ROI, capital requirements and an authoritative INVEST / REVIEW / DECLINE recommendation.
        </p>
        <button
          onClick={generate}
          disabled={generating}
          className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-sm font-semibold hover:opacity-90 card-elev disabled:opacity-50"
          data-testid="ic-generate-btn"
        >
          {generating ? "Generating…" : "Generate Investment Committee Paper"} <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    );
  }

  const paper = state.paper;
  const reco = paper.recommendation || {};
  const style = RECO_STYLE[reco.code] || RECO_STYLE.REVIEW;
  const Icon = style.Icon;

  return (
    <div className="space-y-8" data-testid="ic-paper">
      {/* Recommendation banner */}
      <div className={`rounded-2xl p-6 sm:p-8 ${style.bg} border border-border card-elev`} data-testid="ic-recommendation">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className={`h-12 w-12 rounded-xl grid place-items-center bg-background ${style.text}`}>
              <Icon size={22} weight="fill" />
            </div>
            <div>
              <div className="text-overline text-muted-foreground">Investment Committee Recommendation</div>
              <div className={`font-display text-3xl sm:text-4xl tracking-tight ${style.band}`}>{reco.label || reco.code}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-overline text-muted-foreground">OIS™ Composite</div>
            <div className="font-display text-4xl tracking-tight" data-testid="ic-ois-score">{reco.ois_score ?? "—"}<span className="text-lg text-muted-foreground">/100</span></div>
            <div className="text-xs text-muted-foreground mt-0.5">Grade {reco.ois_grade || "—"} · {reco.confidence || "—"} confidence</div>
          </div>
        </div>
        {reco.rationale && (
          <p className="mt-5 pt-5 border-t border-border/60 text-sm leading-relaxed text-foreground/80">{reco.rationale}</p>
        )}
      </div>

      {/* Executive Summary */}
      <Section title="Executive Summary" number="01" testId="ic-exec-summary">
        <p className="text-sm leading-relaxed">{paper.executive_summary}</p>
      </Section>

      {/* Investment Thesis */}
      <Section title="Investment Thesis" number="02" testId="ic-thesis">
        <p className="text-sm leading-relaxed">{paper.investment_thesis}</p>
      </Section>

      {/* Key Opportunities */}
      <Section title="Key Opportunities" number="03" testId="ic-opportunities">
        <ul className="space-y-2.5">
          {(paper.key_opportunities || []).map((o, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="text-primary font-mono text-xs font-bold mt-1 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="leading-relaxed">{o}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Key Risks */}
      <Section title="Key Risks" number="04" testId="ic-risks">
        <div className="space-y-3">
          {(paper.key_risks || []).map((r, i) => (
            <div key={i} className="border-l-2 border-border pl-4 pr-3 py-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-semibold text-sm">{r.risk}</div>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${SEVERITY_STYLE[r.severity] || "bg-secondary"}`}>{r.severity}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{r.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Mitigation Strategy */}
      <Section title="Mitigation Strategy" number="05" testId="ic-mitigations">
        <ul className="space-y-2.5">
          {(paper.mitigation_strategy || []).map((m, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <ShieldCheck size={14} weight="fill" className="text-teal mt-1 flex-shrink-0" />
              <span className="leading-relaxed">{m}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* SWOT */}
      {paper.swot && (
        <Section title="SWOT" number="06" testId="ic-swot">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: "Strengths", key: "strengths", tone: "text-teal", bg: "bg-teal-soft" },
              { title: "Weaknesses", key: "weaknesses", tone: "text-red-700", bg: "bg-red-100" },
              { title: "Opportunities", key: "opportunities", tone: "text-primary", bg: "bg-primary/10" },
              { title: "Threats", key: "threats", tone: "text-amber-700", bg: "bg-amber-100" },
            ].map((q) => (
              <div key={q.key} className={`rounded-xl p-4 ${q.bg}`}>
                <div className={`text-overline mb-2.5 ${q.tone}`}>{q.title}</div>
                <ul className="space-y-1.5">
                  {(paper.swot[q.key] || []).map((x, i) => (
                    <li key={i} className="text-xs leading-relaxed">· {x}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Entry Strategy */}
      {paper.recommended_entry_strategy && (
        <Section title="Recommended Entry Strategy" number="07" testId="ic-entry">
          <div className="mb-4">
            <div className="text-overline text-muted-foreground">Structure</div>
            <div className="mt-1 text-sm font-semibold">{paper.recommended_entry_strategy.structure}</div>
          </div>
          <div className="mb-4">
            <div className="text-overline text-muted-foreground mb-2">Phased plan</div>
            <ol className="space-y-2">
              {(paper.recommended_entry_strategy.phasing || []).map((p, i) => (
                <li key={i} className="text-sm leading-relaxed pl-8 relative">
                  <span className="absolute left-0 top-0 h-6 w-6 rounded-full bg-primary/10 text-primary font-mono text-[11px] font-bold grid place-items-center">{i + 1}</span>
                  {p}
                </li>
              ))}
            </ol>
          </div>
          {paper.recommended_entry_strategy.governance && (
            <div>
              <div className="text-overline text-muted-foreground">Governance</div>
              <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{paper.recommended_entry_strategy.governance}</div>
            </div>
          )}
        </Section>
      )}

      {/* Expected ROI */}
      {paper.expected_roi && (
        <Section title="Expected ROI" number="08" testId="ic-roi">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[
              { label: "IRR Downside", value: `${paper.expected_roi.irr_downside_pct}%`, tone: "text-red-600" },
              { label: "IRR Base",     value: `${paper.expected_roi.irr_base_pct}%`,     tone: "text-primary" },
              { label: "IRR Upside",   value: `${paper.expected_roi.irr_upside_pct}%`,   tone: "text-teal" },
              { label: "Payback",      value: `${paper.expected_roi.payback_years} yr`,  tone: "text-foreground" },
              { label: "MOIC Base",    value: `${paper.expected_roi.moic_base}×`,        tone: "text-foreground" },
            ].map((m) => (
              <div key={m.label} className="rounded-lg border border-border p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.label}</div>
                <div className={`mt-1 data-large text-xl ${m.tone}`}>{m.value}</div>
              </div>
            ))}
          </div>
          {paper.expected_roi.narrative && (
            <p className="text-sm text-muted-foreground leading-relaxed">{paper.expected_roi.narrative}</p>
          )}
        </Section>
      )}

      {/* Capital Requirements */}
      {paper.capital_requirements && (
        <Section title="Capital Requirements" number="09" testId="ic-capital">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Metric label="Initial deployment" value={paper.capital_requirements.initial_eur} />
            <Metric label="Follow-on" value={paper.capital_requirements.follow_on_eur} />
            <Metric label="Total programme" value={paper.capital_requirements.total_eur} tone="text-primary" />
          </div>
          <div>
            <div className="text-overline text-muted-foreground mb-2">Use of funds</div>
            <ul className="space-y-1.5">
              {(paper.capital_requirements.use_of_funds || []).map((u, i) => (
                <li key={i} className="text-sm">· {u}</li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      {/* Regenerate & timestamp footer */}
      <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground border-t border-border">
        <div>
          Generated {state.generated_at ? new Date(state.generated_at).toLocaleString() : "just now"} · Recommendation code is authoritative and computed from the OIS engine.
        </div>
        <button
          onClick={generate}
          disabled={generating}
          className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
          data-testid="ic-regenerate-btn"
        >
          {generating ? "Regenerating…" : "Regenerate paper"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, number, children, testId }) {
  return (
    <section className="bg-card border border-border rounded-2xl p-6 sm:p-7 card-elev" data-testid={testId}>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-[10px] font-mono font-bold text-primary tracking-widest">{number}</span>
        <h3 className="font-display text-lg sm:text-xl tracking-tight">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Metric({ label, value, tone = "text-foreground" }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-overline text-muted-foreground">{label}</div>
      <div className={`mt-1 data-large text-xl ${tone}`}>{value}</div>
    </div>
  );
}
